import React from 'react'
import { SliderControl } from './SliderControl'
import { SegmentedControl } from './SegmentedControl'
import { SpringCurveVisualizer } from '../visualizers/SpringCurveVisualizer'
import { EasingCurveVisualizer } from '../visualizers/EasingCurveVisualizer'
import { SPRING_RANGES, TWEEN_RANGES, INERTIA_RANGES, EASING_PRESETS } from '../../constants'
import type { TransitionConfig, TransitionType, SpringConfig, InertiaConfig } from '../../types'

interface TransitionSectionProps {
  transition: TransitionConfig
  progress: number
  onTypeChange: (type: TransitionType) => void
  onSpringChange: (payload: Partial<SpringConfig>) => void
  onTweenDurationChange: (duration: number) => void
  onTweenEaseChange: (ease: string) => void
  onInertiaChange: (payload: Partial<InertiaConfig>) => void
}

const TYPE_OPTIONS = [
  { label: 'Spring', value: 'spring' as TransitionType },
  { label: 'Tween', value: 'tween' as TransitionType },
  { label: 'Inertia', value: 'inertia' as TransitionType },
]

const SPRING_LABELS: Record<keyof typeof SPRING_RANGES, string> = {
  stiffness: 'Stiffness',
  damping: 'Damping',
  mass: 'Mass',
  bounce: 'Bounce',
}

const INERTIA_LABELS: Record<keyof typeof INERTIA_RANGES, string> = {
  velocity: 'Velocity',
  power: 'Power',
  min: 'Min Bound',
  max: 'Max Bound',
}

export function TransitionSection({
  transition,
  progress,
  onTypeChange,
  onSpringChange,
  onTweenDurationChange,
  onTweenEaseChange,
  onInertiaChange,
}: TransitionSectionProps) {
  return (
    <div className="space-y-4">
      <SegmentedControl
        options={TYPE_OPTIONS}
        value={transition.type}
        onChange={onTypeChange}
      />

      {transition.type === 'spring' && (
        <div className="space-y-3">
          <SpringCurveVisualizer config={transition.spring} progress={progress} />
          {(Object.keys(SPRING_RANGES) as (keyof typeof SPRING_RANGES)[]).map((key) => {
            const range = SPRING_RANGES[key]
            return (
              <SliderControl
                key={key}
                label={SPRING_LABELS[key]}
                value={transition.spring[key]}
                min={range.min}
                max={range.max}
                step={range.step}
                onChange={(val) => onSpringChange({ [key]: val })}
              />
            )
          })}
        </div>
      )}

      {transition.type === 'tween' && (
        <div className="space-y-3">
          <EasingCurveVisualizer config={transition.tween} progress={progress} />
          <SliderControl
            label="Duration"
            value={transition.tween.duration}
            min={TWEEN_RANGES.duration.min}
            max={TWEEN_RANGES.duration.max}
            step={TWEEN_RANGES.duration.step}
            unit={TWEEN_RANGES.duration.unit}
            onChange={onTweenDurationChange}
          />
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Easing
            </label>
            <select
              value={transition.tween.ease}
              onChange={(e) => onTweenEaseChange(e.target.value)}
              className="w-full px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400
                cursor-pointer"
            >
              {EASING_PRESETS.map((ease) => (
                <option key={ease} value={ease}>
                  {ease}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {transition.type === 'inertia' && (
        <div className="space-y-3">
          {(Object.keys(INERTIA_RANGES) as (keyof typeof INERTIA_RANGES)[]).map((key) => {
            const range = INERTIA_RANGES[key]
            return (
              <SliderControl
                key={key}
                label={INERTIA_LABELS[key]}
                value={transition.inertia[key]}
                min={range.min}
                max={range.max}
                step={range.step}
                onChange={(val) => onInertiaChange({ [key]: val })}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
